import { useParams, Link } from 'react-router-dom';
import { useOrder } from '@/hooks/useOrders';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { OrderStatusBadge } from '@/components/orders/OrderStatusBadge';
import { OrderProgress } from '@/components/orders/OrderProgress';
import { Button } from '@/components/ui/Button';
import { api } from '@/services/api';

export const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading, error } = useOrder(id ?? null);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div
        className="container-main text-center"
        style={{ paddingTop: 'var(--space-12)', paddingBottom: 'var(--space-12)' }}
      >
        <h2
          style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
          }}
        >
          Order Not Found
        </h2>
        <Link
          to="/orders"
          style={{
            marginTop: 'var(--space-4)',
            display: 'inline-block',
            color: 'var(--text-accent)',
            textDecoration: 'none',
          }}
        >
          Back to Orders
        </Link>
      </div>
    );
  }

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '896px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      <Link
        to="/orders"
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-accent)',
          textDecoration: 'none',
        }}
      >
        &larr; Back to Orders
      </Link>

      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between"
        style={{ marginTop: 'var(--space-6)', marginBottom: 'var(--space-8)', gap: 'var(--space-3)' }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Order #{order.orderId.slice(-8).toUpperCase()}
          </h1>
          <p
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
            }}
          >
            Placed {new Date(order.createdAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
          <OrderStatusBadge status={order.status} />
          {order.status === 'delivered' && order.quoteId && (
            <Link
              to={`/quote?reorder=${order.quoteId}${order.materialId ? `&material=${order.materialId}` : ''}`}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="secondary" size="sm">Reorder</Button>
            </Link>
          )}
          {order.status !== 'cancelled' && order.status !== 'rejected' && (
            <ReceiptButton orderId={order.orderId} />
          )}
        </div>
      </div>

      {/* Order progress stepper */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <OrderProgress status={order.status} />
      </div>

      {/* Cancellation/rejection notice */}
      {(order.status === 'cancelled' || order.status === 'rejected') && (
        <div
          style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-error-200, #fecaca)',
            backgroundColor: '#fef2f2',
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-6)',
          }}
        >
          <p style={{ fontWeight: 'var(--font-weight-semibold)', color: '#991b1b' }}>
            {order.status === 'cancelled' ? 'Order Cancelled' : 'Order Rejected'}
          </p>
          {order.cancellationReason && (
            <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-sm)', color: '#991b1b' }}>
              Reason: {order.cancellationReason}
            </p>
          )}
          <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-sm)', color: '#7f1d1d' }}>
            A refund has been initiated and will appear on your statement within 5-10 business days.
          </p>
        </div>
      )}

      <div
        className="grid md:grid-cols-2"
        style={{ gap: 'var(--space-8)' }}
      >
        {/* Order details */}
        <div
          style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            padding: 'var(--space-6)',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-4)',
            }}
          >
            Details
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <DetailRow label="Type" value={order.type === 'quote' ? 'Custom Print' : 'Catalog Product'} />
            {order.materialId && (
              <DetailRow label="Material" value={order.materialId.toUpperCase()} />
            )}
            <DetailRow label="Quantity" value={String(order.quantity)} />
            <div
              style={{
                borderTop: '1px solid var(--border-default)',
                paddingTop: 'var(--space-3)',
                marginTop: 'var(--space-1)',
              }}
              className="flex items-center justify-between"
            >
              <span
                style={{
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                Total
              </span>
              <span
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-primary-600)',
                }}
              >
                ${order.totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Shipping address */}
        {order.shippingAddress && (
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
              padding: 'var(--space-6)',
            }}
          >
            <h2
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-primary)',
                marginBottom: 'var(--space-4)',
              }}
            >
              Shipping Address
            </h2>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              <p style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                {order.shippingAddress.name}
              </p>
              <p>{order.shippingAddress.line1}</p>
              {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
              <p>
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}
              </p>
              <p>{order.shippingAddress.country}</p>
            </div>
          </div>
        )}

        {/* Tracking / Shipping info — only show when there's actual data */}
        {(order.shipping?.trackingNumber || order.shipping?.carrier || order.shipping?.dateShipped || order.trackingNumber) && (
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
              padding: 'var(--space-6)',
            }}
          >
            <h2
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-primary)',
                marginBottom: 'var(--space-4)',
              }}
            >
              Shipping & Tracking
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {order.shipping?.carrier && (
                <DetailRow label="Carrier" value={order.shipping.carrier} />
              )}
              {order.shipping?.shippingService && (
                <DetailRow label="Service" value={order.shipping.shippingService} />
              )}
              {(order.shipping?.trackingNumber ?? order.trackingNumber) && (
                <DetailRow label="Tracking" value={order.shipping?.trackingNumber ?? order.trackingNumber!} />
              )}
              {!order.shipping?.trackingNumber && order.trackingCarrier && (
                <DetailRow label="Carrier" value={order.trackingCarrier} />
              )}
              {order.shipping?.dateShipped && (
                <DetailRow label="Shipped" value={new Date(order.shipping.dateShipped).toLocaleDateString()} />
              )}
              {order.shipping?.deliveredDate && (
                <DetailRow label="Delivered" value={new Date(order.shipping.deliveredDate).toLocaleDateString()} />
              )}
            </div>
          </div>
        )}

        {/* Print job summary */}
        {order.printJob && (
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
              padding: 'var(--space-6)',
            }}
          >
            <h2
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-primary)',
                marginBottom: 'var(--space-4)',
              }}
            >
              Print Details
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {order.printJob.originalFileName && <DetailRow label="File" value={order.printJob.originalFileName} />}
              {order.printJob.materialId && <DetailRow label="Material" value={order.printJob.materialId.toUpperCase()} />}
              {order.printJob.color && <DetailRow label="Color" value={order.printJob.color} />}
              {order.printJob.dimensions && (
                <DetailRow label="Dimensions" value={`${order.printJob.dimensions.width} x ${order.printJob.dimensions.height} x ${order.printJob.dimensions.depth} cm`} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ReceiptButton = ({ orderId }: { orderId: string }) => {
  const handleDownload = async () => {
    try {
      const html = await api.getRaw(`/orders/${orderId}/receipt`);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${orderId.slice(-8)}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      // Silently fail — receipt not available
    }
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleDownload}>
      Receipt
    </Button>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between">
    <span
      style={{
        fontSize: 'var(--text-sm)',
        color: 'var(--text-tertiary)',
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-primary)',
      }}
    >
      {value}
    </span>
  </div>
);
